import { useContext, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Badge, Spinner, ProgressBar, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { WebSocketContext } from '../../providers/WebSocketProvider';
import SearchBox from '../../components/common/SearchBox';
import {
  useOperatorLive,
  useOperatorRoster
} from '../../features/operator-dashboard/hooks/useOperatorDashboard';

const fmt = (v, d = 0) =>
  Number(v || 0).toLocaleString('ro-RO', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtDur = sec => {
  const s = Number(sec || 0);
  if (!s) return '0m';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Same speed dialects Machine Monitor reads — every PLC names it differently.
const getSpeed = cps =>
  Number(
    cps?.LineSpeed ??
      cps?.LINE_SPEED ??
      cps?.LINE_SPEED_ACT ??
      cps?.ACT_LINE_SPEED ??
      cps?.ACT_SPEED ??
      cps?.ACT_RAMP_VALUE_PER ??
      0
  );

const statusTone = s => {
  const u = String(s || '').toLowerCase();
  if (u === 'running') return 'success';
  if (u === 'inactive') return 'warning';
  return 'danger';
};

const KpiCard = ({ title, value, sub, tone = 'primary' }) => (
  <Card className="h-100">
    <Card.Body className="py-3 px-3">
      <p className="text-700 fs-10 mb-1">{title}</p>
      <h3 className={`mb-0 text-${tone}`}>{value}</h3>
      {sub && <p className="text-700 fs-10 mb-0 mt-1">{sub}</p>}
    </Card.Body>
  </Card>
);

/**
 * One person at one machine.
 *
 * The card leads with the operator rather than the machine — that is the whole
 * difference from Machine Monitor. A station producing with nobody logged in is
 * shown as a gap rather than hidden, because those metres end up unattributed.
 */
const StationCard = ({ station, plc, shiftRow }) => {
  const speed = getSpeed(plc?.currentPlcState);
  const status = plc?.status?.label || (station.working ? 'running' : 'stopped');
  const unmanned = !station.operator;

  const progress =
    station.plannedLength > 0
      ? Math.min(100, (station.producedLength / station.plannedLength) * 100)
      : 0;

  return (
    <Card className={`h-100 ${unmanned ? 'border border-warning' : ''}`}>
      <Card.Body className="py-3 px-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="min-w-0">
            <h5 className={`mb-0 text-truncate ${unmanned ? 'text-warning' : ''}`}>
              {station.operator || 'Nobody logged in'}
            </h5>
            <span className="text-700 fs-10 text-uppercase">{station.machineId}</span>
          </div>
          <Badge bg={statusTone(status)} className="text-uppercase fs-10">
            {status}
          </Badge>
        </div>

        {station.working ? (
          <>
            <div className="mb-2">
              {station.jobId ? (
                <Link
                  to={`/apps/project-management/project-details/${station.jobId}`}
                  className="fw-semibold fs-9 text-decoration-none"
                >
                  {station.productLabel || station.jobCode || '—'}
                </Link>
              ) : (
                <span className="fw-semibold fs-9">{station.productLabel || '—'}</span>
              )}
              {station.phase && (
                <Badge bg="primary" variant="phoenix" className="ms-2 fs-10">
                  {station.phase.replaceAll('_', ' ')}
                </Badge>
              )}
            </div>

            <div className="d-flex justify-content-between fs-10 text-700 mb-1">
              <span>{station.tokenId || '—'}</span>
              <span>
                {fmt(station.producedLength)} / {fmt(station.plannedLength)} m
              </span>
            </div>
            <ProgressBar now={progress} style={{ height: 4 }} className="mb-3" />

            <Row className="g-2 text-center">
              <Col xs={4}>
                <div className="fs-10 text-700">Speed</div>
                <div className="fw-bold fs-9">{fmt(speed, 1)}</div>
                <div className="fs-10 text-700">m/min</div>
              </Col>
              <Col xs={4}>
                <div className="fs-10 text-700">On this drum</div>
                <div className="fw-bold fs-9">{fmt(station.operatorMetersOnDrum)}</div>
                <div className="fs-10 text-700">m</div>
              </Col>
              <Col xs={4}>
                <div className="fs-10 text-700">Running</div>
                <div className="fw-bold fs-9">{fmtDur(station.runningSec)}</div>
                <div className="fs-10 text-700">this drum</div>
              </Col>
            </Row>

            {/* A drum handed over mid-run belongs to more than one person. */}
            {station.onDrum.length > 1 && (
              <div className="mt-3 pt-2 border-top border-translucent">
                <div className="fs-10 text-700 mb-1">Shared drum</div>
                {station.onDrum.map(o => (
                  <div key={o.operator} className="d-flex justify-content-between fs-10">
                    <span className={o.operator === '(unassigned)' ? 'text-warning' : ''}>
                      {o.operator}
                    </span>
                    <span className="text-700">{fmt(o.meters)} m</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-700 fs-9 py-3 text-center">At the machine, nothing running</div>
        )}

        {shiftRow && (
          <div className="mt-3 pt-2 border-top border-translucent d-flex justify-content-between fs-10">
            <span className="text-700">This shift</span>
            <span className="fw-semibold">
              {fmt(shiftRow.meters)} m · {shiftRow.drums} drum(s)
            </span>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const OperatorMonitor = () => {
  const { machineData, subscribeToChannel, unsubscribeToChannel } = useContext(WebSocketContext);
  const liveQ = useOperatorLive();
  const rosterQ = useOperatorRoster();
  const [search, setSearch] = useState('');

  useEffect(() => {
    subscribeToChannel('machine-all');
    return () => unsubscribeToChannel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plcByMachine = useMemo(() => {
    const map = new Map();
    for (const m of machineData?.state || []) map.set(m.machine, m);
    return map;
  }, [machineData]);

  const stations = useMemo(() => liveQ.data?.stations || [], [liveQ.data]);
  const shiftTotals = useMemo(() => liveQ.data?.shiftTotals || [], [liveQ.data]);
  const shiftByOperator = useMemo(
    () => new Map(shiftTotals.map(r => [r.operator, r])),
    [shiftTotals]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stations;
    return stations.filter(s =>
      [s.operator, s.machineId, s.productLabel, s.phase]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [stations, search]);

  const atMachine = new Set(stations.map(s => s.operator).filter(Boolean));
  const shift = liveQ.data?.shift;

  // Registered for the shift in progress but not logged in anywhere. Only shown
  // when the roster loads — it lives in another service and is allowed to fail.
  const idle = useMemo(() => {
    const roster = Array.isArray(rosterQ.data) ? rosterQ.data : [];
    return roster
      .filter(o => !atMachine.has(o.name))
      .filter(o => !shift || !o.shift || o.shift === shift);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterQ.data, stations, shift]);

  const workingCount = stations.filter(s => s.working && s.operator).length;
  const unmanned = stations.filter(s => s.working && !s.operator);
  const shiftMeters = shiftTotals.reduce((s, r) => s + r.meters, 0);

  if (liveQ.isLoading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-700">Loading operator state...</p>
      </div>
    );
  }

  return (
    <div className="mb-9">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Operator Monitor</h2>
          <p className="text-700 mb-0">
            Who is on what right now{shift ? ` — Shift ${shift}` : ''}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Badge bg="success" className="px-3 py-2 fs-9">{workingCount} Working</Badge>
          <Badge bg="secondary" className="px-3 py-2 fs-9">{idle.length} Off station</Badge>
          {unmanned.length > 0 && (
            <Badge bg="warning" className="px-3 py-2 fs-9">{unmanned.length} Unmanned</Badge>
          )}
        </div>
      </div>

      {liveQ.isError && (
        <Alert variant="danger">
          Failed to load live operator state: {liveQ.error?.message || 'Unknown error'}
        </Alert>
      )}

      {unmanned.length > 0 && (
        <Alert variant="warning" className="py-2 fs-9">
          <strong>{unmanned.map(s => s.machineId.toUpperCase()).join(', ')}</strong>{' '}
          {unmanned.length === 1 ? 'is' : 'are'} producing with nobody logged in — those metres
          will land under <em>(unassigned)</em> until somebody is set at the machine.
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <KpiCard
            title="Produced this shift"
            value={`${fmt(shiftMeters)} m`}
            sub={`${shiftTotals.length} operator(s) contributed`}
          />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="At a machine" value={`${workingCount}/${stations.length}`} tone="success" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Best rate"
            value={shiftTotals[0] ? `${fmt(shiftTotals[0].metersPerHour)} m/h` : '—'}
            sub={shiftTotals[0]?.operator}
            tone="info"
          />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Shift"
            value={shift || '—'}
            sub="Changes at 07:00 / 19:00"
            tone="warning"
          />
        </Col>
      </Row>

      <div className="mb-3">
        <SearchBox
          placeholder="Search operator, machine, product, phase..."
          onChange={e => setSearch(e.target.value || '')}
        />
      </div>

      <Row className="g-3 mb-4">
        {filtered.map(s => (
          <Col xs={12} sm={6} lg={4} xxl={3} key={s.machineId}>
            <StationCard
              station={s}
              plc={plcByMachine.get(s.machineId)}
              shiftRow={s.operator ? shiftByOperator.get(s.operator) : null}
            />
          </Col>
        ))}
        {!filtered.length && (
          <Col xs={12}>
            <Card>
              <Card.Body className="text-center text-700 py-5">
                Nobody is set at a machine right now.
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      <Row className="g-3">
        <Col xs={12} lg={7}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">This shift, per operator</h6>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr className="text-700 fs-10 text-uppercase">
                      <th>Operator</th>
                      <th className="text-end">Produced</th>
                      <th className="text-end">Drums</th>
                      <th className="text-end">Running</th>
                      <th className="text-end">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shiftTotals.map(r => (
                      <tr key={r.operator}>
                        <td className={r.operator === '(unassigned)' ? 'text-warning' : ''}>
                          {r.operator}
                        </td>
                        <td className="text-end fw-semibold">{fmt(r.meters)} m</td>
                        <td className="text-end">{r.drums}</td>
                        <td className="text-end">{fmtDur(r.runningSec)}</td>
                        <td className="text-end">{fmt(r.metersPerHour)} m/h</td>
                      </tr>
                    ))}
                    {!shiftTotals.length && (
                      <tr>
                        <td colSpan={5} className="text-center text-700 py-4">
                          Nothing produced yet this shift.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={5}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">Off station{shift ? ` — shift ${shift}` : ''}</h6>
            </Card.Header>
            <Card.Body>
              {rosterQ.isError ? (
                <p className="text-700 fs-9 mb-0">
                  Roster unavailable — the live view above does not depend on it.
                </p>
              ) : idle.length ? (
                <div className="d-flex flex-wrap gap-2">
                  {idle.map(o => (
                    <Badge key={o._id || o.name} bg="secondary" variant="phoenix" className="fs-9">
                      {o.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-700 fs-9 mb-0">Everybody on this shift is at a machine.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OperatorMonitor;
